// ============================================================
// vialResolution - the ONE place that answers "which physical vial
// is this dose coming out of?"
//
// Before this module existed the answer lived in three diverging
// copies (DoseLogModal, desktop LogDoseDrawer, ad-hoc modal), and
// the linked-id copy had a trap: fetchInventoryById returns ARCHIVED
// vials too, so a protocol still pointing at a dead vial (the
// mark-empty bug, P0-3) got a non-null result, skipped the active
// fallback, and the dose never decremented anything again.
//
//   component.inventoryId ──> fetch by id
//        │                        │
//        │                 active? ──yes──> that vial
//        │                        │
//        │                 archived/missing
//        │                        │
//        └── none ────────────────┤
//                                 ▼
//                     combined group?  ──yes──> null (never guess a mix)
//                                 │
//                                 no
//                                 ▼
//              newest active, reconstituted, non-mix
//              vial of this peptide (or null)
//
// (Vial lifecycle review 2026-07-08, decisions 3A/4A/9A.)
// ============================================================

import type { InventoryItem } from './types';
import { fetchInventoryById, fetchUsableVialForPeptide } from './cloud';

export interface VialResolutionInput {
  /** The component's persisted vial link, if any. */
  inventoryId?: string | null;
  /** Peptide to fall back on for singles. */
  peptideId: string;
}

/**
 * Resolve the vial a dose should decrement. Returns null when no
 * usable vial exists - callers log the dose with no inventory write
 * (the RPCs accept a null inventory id for exactly this).
 *
 * `isCombined` MUST be true for combined-vial (mix) administrations:
 * mixes only ever resolve through their explicit link. A peptide-name
 * fallback would happily hand back a plain single vial of the lead
 * peptide, which is not the vial the mix is drawn from.
 */
export async function resolveVialForComponent(
  component: VialResolutionInput,
  opts: { isCombined: boolean },
): Promise<InventoryItem | null> {
  if (component.inventoryId) {
    const linked = await fetchInventoryById(component.inventoryId);
    if (linked && linked.isActive) return linked;
    // Linked vial archived or gone. For singles, fall through to the
    // active-vial search (self-healing after mark-empty). For mixes,
    // stop here.
  }
  if (opts.isCombined) return null;
  return fetchUsableVialForPeptide(component.peptideId);
}
